import { Box, Checkbox, Divider, HStack, Link, Text, VStack } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { ROUTES } from 'router/routes';

type TermsAndConditionsProps = {
  agreed: boolean;
  onAgreedChange: (agreed: boolean) => void;
  termAndConditions: string[];
};

/**
 * The checkout terms gate, per `Ticket Zone New Direction Terms.png`: a **blush** card (no border)
 * holding a right-aligned heading, the event's bullets, a hairline, and the agreement checkbox with
 * its explanatory line beneath.
 *
 * Everything is right-aligned rather than centred, because the design sets the whole card flush to
 * the RTL reading edge — `textAlign='start'` resolves to the right under `dir='rtl'`, where `end`
 * would push it left.
 *
 * The purchase CTA is deliberately **not** inside this card: the design's rounded corners close
 * immediately below the explanatory line, so the button keeps its own surface in `CheckoutSummary`.
 */
export function TermsAndConditions({
  agreed,
  onAgreedChange,
  termAndConditions,
}: TermsAndConditionsProps) {
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onAgreedChange(event.target.checked);
  };

  return (
    <Box width='full' bg='checkout.termsBg' borderRadius='16px' p={{ base: '20px', md: '24px' }}>
      <VStack spacing='16px' align='stretch' dir='rtl'>
        <Text variant='bodyMediumSemiBold' color='dark.D900' fontWeight='bold' textAlign='start'>
          <FormattedMessage id='checkout.termsHeader' defaultMessage={'Terms and conditions'} />
        </Text>

        <VStack spacing='10px' align='stretch'>
          {termAndConditions.map((term: string, index: number) => (
            <HStack key={index} spacing='10px' align='start'>
              {/* Warm grey dot, not the brand teal — the design keeps the bullets neutral so the
                  card's only colour is its own surface. */}
              <Box minW='6px' h='6px' borderRadius='full' bg='checkout.termsBullet' mt='7px' />
              <Text variant='bodySmallRegular' color='dark.D700' lineHeight='1.6' flex='1'>
                {term}
              </Text>
            </HStack>
          ))}
        </VStack>

        <Divider borderColor='blackAlpha.100' />

        <VStack spacing='6px' align='stretch'>
          <Checkbox
            isChecked={agreed}
            onChange={handleCheckboxChange}
            size='md'
            colorScheme='green'
            spacing='12px'
            alignItems='center'
          >
            <Text variant='bodySmallMedium' color='dark.D900' fontWeight='bold'>
              <FormattedMessage
                id='checkout.iAgree'
                defaultMessage={'I agree to the terms and conditions'}
              />
            </Text>
          </Checkbox>

          <Text variant='caption' color='dark.D500' textAlign='start' lineHeight='1.5'>
            <FormattedMessage
              id='checkout.byClickingHere'
              defaultMessage={
                'By clicking here, I state that I have read and understood the <link>Terms and conditions</link>'
              }
              values={{
                link: (children) => (
                  <Link
                    as={RouterLink}
                    to={ROUTES.termsOfUse}
                    color='green.G500'
                    textDecoration='underline'
                    display='inline'
                    position='relative'
                    zIndex={10}
                  >
                    {children}
                  </Link>
                ),
              }}
            />
          </Text>
        </VStack>
      </VStack>
    </Box>
  );
}
